import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';

type Props = {
  accept: string;
  label: string;
  onFile: (file: File) => void;
  disabled?: boolean;
};

/** A click-or-drag file picker. Calls `onFile` once per drop/selection (the caller validates/resizes). */
export function Dropzone({ accept, label, onFile, disabled }: Props) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragActive(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file) onFile(file);
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) onFile(file);
    e.target.value = '';
  }

  return (
    <div
      className={`dropzone ${dragActive ? 'drag-active' : ''}`}
      onClick={() => !disabled && inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragActive(true);
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={handleDrop}
      role="button"
      tabIndex={0}
    >
      <input ref={inputRef} type="file" accept={accept} hidden onChange={handleChange} disabled={disabled} />
      {label}
    </div>
  );
}
